import { Stack, Typography, Divider, Box } from '@mui/material';
import type { GetGcsServiceAgentResponse } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { useDAL } from '@/contexts/useDAL';
import type { BackupVault } from '@/data/vaults/backupVault';
import { getDocsUrl } from '@/docs/docsUrls';

import { generateGcpKmsCommands } from './gcpKmsCommands';

import { BottomDialog } from '../dialogs/bottomDialog';
import { FormTextField } from '../form/formTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { Loader } from '../layout/loading';
import { PanelWrapper } from '../panels/panelWrapper';
import { CopyableCode } from '../shared/copyableCode';
import { ReadMoreLink } from '../shared/readMoreLink';

const gcpKmsKeyConfig = getCloudFieldConfig(Provider.Gcp, 'kmsKeyResourceName');

function GcpCmekDescription() {
  return (
    <Typography variant='body1' component={'span'}>
      Set the Cloud KMS key to encrypt snapshots with. You&apos;re responsible
      for securing the key from unauthorized access and deletion. Without this
      key, snapshots can&apos;t be retrieved from the vault.{' '}
      <ReadMoreLink href={getDocsUrl('vaultSelfManagedKeys')} />
    </Typography>
  );
}

export function GcpKmsKeyEditorPanel({
  open,
  onClose,
  data,
  setData,
  scanningAccountId,
}: {
  open: boolean;
  onClose: () => void;
  data: BackupVault;
  setData: (data: BackupVault) => void;
  scanningAccountId: string;
}) {
  const dal = useDAL();
  const { data: serviceAgent, isLoading } =
    dal.cloudAccounts.scanning.useGetGcsServiceAgent(scanningAccountId);
  const encryptionKey = data.vaultAttributes.gcp?.encryptionKey ?? '';
  const isKeyValid = encryptionKey.trim().length > 0;

  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      vaultAttributes: {
        ...data.vaultAttributes,
        gcp: {
          ...data.vaultAttributes.gcp,
          encryptionKey: event.target.value,
        },
      },
    });
  };

  return (
    <BottomDialog open={open} isFullScreen={true}>
      <PanelWrapper
        header={{
          title: 'Set Cloud KMS Key',
          subtitle: <GcpCmekDescription />,
          fullWidthSubtitle: false,
          onClose: onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Done',
            'data-testid': 'done-button',
            onClick: onClose,
            disabled: !isKeyValid,
          },
          secondaryButton: {
            text: 'Cancel',
            'data-testid': 'cancel-button',
            onClick: onClose,
          },
        }}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <Box padding='40px min(100px,6vw)' width='100%' height='auto'>
            <Stack
              gap='40px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <KeyDetailsSection
                encryptionKey={encryptionKey}
                onChange={handleKeyChange}
              />
              {serviceAgent && (
                <KmsAccessSection
                  encryptionKey={encryptionKey}
                  serviceAgent={serviceAgent ?? {}}
                />
              )}
            </Stack>
          </Box>
        )}
      </PanelWrapper>
    </BottomDialog>
  );
}

function KeyDetailsSection({
  encryptionKey,
  onChange,
}: {
  encryptionKey: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <NumericBulletSection bulletNumber={1} title='Set key details'>
      <BulletItem
        leftContent={
          <FormTextField
            title='CMEK key resource name'
            placeholder={gcpKmsKeyConfig.placeholder}
            value={encryptionKey}
            onChange={onChange}
            validation={gcpKmsKeyConfig.validation}
          />
        }
      />
    </NumericBulletSection>
  );
}

function KmsAccessSection({
  encryptionKey,
  serviceAgent,
}: {
  encryptionKey: string;
  serviceAgent: GetGcsServiceAgentResponse;
}) {
  const kmsCommands = generateGcpKmsCommands(encryptionKey, {
    scanningServiceAccount: serviceAgent.scanningServiceAccount,
    gcsServiceAgent: serviceAgent.gcsServiceAgent,
    restoreServiceAccounts: serviceAgent.restoreServiceAccounts,
  });

  return (
    <NumericBulletSection
      bulletNumber={2}
      title='Grant KMS key access for vault'
    >
      <BulletItem
        title='Grant the required service accounts access to your KMS key. These permissions enable Eon to create, manage, and restore encrypted data in the vault.'
        leftContent={<Box />}
        rightContent={
          <CopyableCode title='vault_kms_perm.sh' code={kmsCommands} />
        }
      />
    </NumericBulletSection>
  );
}
